import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Alert, Box, Skeleton, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';

import { AuthStoreContext } from '../Root';
import DeviceHistoryStore from '../../stores/device-history-store';
import { DeviceService } from '../../http/device-service';
import TimeIntervalSelector, { defaultIntervalState, IntervalState } from '../dashboard/TimeIntervalSelector';
import Kosa from '../dashboard/kosa/Kosa';
import { DashboardOutletContext } from '../dashboard/DashboardShellLayout';
import { SensorType, SensorState, sensorStateStr } from '../../types/sensor';
import { logConfigStr } from '../../types/log-config';
import { getDeviceStatusColor } from '../../utils/device-status';
import { timePassed, timeStr } from '../../utils/time-functions';
import { TelemetryPoint } from './DeviceTelemetryChart';
import DeviceMetricRow from './DeviceMetricRow';
import DeviceLogs from './DeviceLogs';

//=================================================================================================

function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0';
    const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    let i = 0;
    let v = bytes;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

// ─── Строка таблицы без бордера ──────────────────────────────────────────────

const DeviceRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1,
            minWidth: 0,
        }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, width: 260 }}>
            {label}:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>{children}</Box>
    </Box>
);

//=================================================================================================

const DeviceBoardLayout = observer(function DeviceBoardLayout() {
    const { id } = useParams<{ id: string }>();
    const deviceId = Number(id);
    const location = useLocation();
    const nodeId = (location.state as { nodeId?: number } | null)?.nodeId;

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { authStore } = useContext(AuthStoreContext);
    const isRoot = authStore.user?.isRoot() ?? false;

    const [store, setStore] = useState(() => new DeviceHistoryStore(deviceId));
    const [intervalState, setIntervalState] = useState<IntervalState>(defaultIntervalState);
    const [error, setError] = useState<string | null>(null);
    const { onSelect, selectSilent } = useOutletContext<DashboardOutletContext>();

    useEffect(() => {
        setStore(new DeviceHistoryStore(deviceId));
        setIntervalState(defaultIntervalState);
        setError(null);

        // Подсвечиваем устройство в сайдбаре, не покидая страницу статистики.
        // nodeId передаётся через state при переходе по кнопке «статистика» из сайдбара,
        // а при прямом входе по URL (state пустой) подгружаем его по deviceId.
        const selectDevice = (nid: number) => {
            if (nid !== undefined) selectSilent({ kind: 'device', deviceId, nodeId: nid });
        };
        if (nodeId !== undefined) {
            selectDevice(nodeId);
        } else {
            DeviceService.byId(deviceId)
                .then((res) => selectDevice(res.data.node_id))
                .catch(() => {
                    /* устройство недоступно — просто не подсвечиваем */
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    useEffect(() => {
        const loadHistory = async () => {
            setError(null);
            try {
                await store.load({
                    from: intervalState.from.toDate(),
                    to: intervalState.to.toDate(),
                });
            } catch (e) {
                setError('Не удалось загрузить историю устройства');
            }
        };
        loadHistory();
    }, [store, intervalState]);

    const handleTimeInterval = useCallback((state: IntervalState) => {
        setIntervalState(state);
    }, []);

    const hasData = store.items.length > 0;

    const lastTransactionRelative = timePassed(store.lastTransaction);
    const lastTransactionTooltip = timeStr(store.lastTransaction);
    const statusColor = getDeviceStatusColor(store.lastTransaction, store.logConfig);

    const isKosaSensor = store.sensorType === SensorType.KosaSensorType;

    // рендер графиков отложен на ~100 мс после последнего изменения данных.
    const [deferredPoints, setDeferredPoints] = useState({
        memory: [] as TelemetryPoint[],
        battery: [] as TelemetryPoint[],
        rssi: [] as TelemetryPoint[],
        cpuTemp: [] as TelemetryPoint[],
    });

    const deferTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        if (deferTimer.current) clearTimeout(deferTimer.current);
        deferTimer.current = setTimeout(() => {
            setDeferredPoints({
                memory: store.items
                    .filter((item) => item.memoryInfo !== undefined)
                    .map((item) => ({
                        time: item.lastTransaction,
                        value: item.memoryInfo!.used,
                    })),
                battery: store.items
                    .filter((item) => item.battery !== undefined)
                    .map((item) => ({
                        time: item.lastTransaction,
                        value: item.battery!.chargeLevel,
                    })),
                rssi: store.items
                    .filter((item) => item.rssi !== undefined)
                    .map((item) => ({
                        time: item.lastTransaction,
                        value: item.rssi!,
                    })),
                cpuTemp: store.items
                    .filter((item) => item.cpuTemp !== undefined)
                    .map((item) => ({
                        time: item.lastTransaction,
                        value: item.cpuTemp!,
                    })),
            });
        }, 100);

        return () => {
            if (deferTimer.current) clearTimeout(deferTimer.current);
        };
    }, [store.items]);

    // последнее значение набора точек (по времени)
    const lastValue = useCallback(
        (pts: TelemetryPoint[]) =>
            pts.length ? [...pts].sort((a, b) => a.time.localeCompare(b.time)).at(-1)!.value : undefined,
        [],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <TimeIntervalSelector state={intervalState} onChange={handleTimeInterval} />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    flexWrap: 'wrap',
                    willChange: 'transform',
                }}
            >
                {store.loading && !hasData ? (
                    <Skeleton variant="text" width={220} height={36} />
                ) : (
                    <Typography variant="h5" fontWeight={600}>
                        {store.deviceName}
                    </Typography>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mt: 2, mx: 2 }}>
                    {error}
                </Alert>
            )}

            {store.loading && !hasData && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Загрузка…
                    </Typography>
                </Box>
            )}

            {!store.loading && !error && !hasData && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Нет данных для отображения
                    </Typography>
                </Box>
            )}

            {hasData && (
                <Box
                    sx={{
                        mt: 2,
                        px: { xs: 2, md: 3 },
                        overflow: 'hidden',
                        minWidth: 0,
                        maxWidth: isDesktop ? 720 : '100%',
                    }}
                >
                    {/* ─── Основная информация ─────────────────────────────────── */}
                    <Box sx={{ '& > * + *': { borderTop: '1px solid', borderTopColor: 'divider' } }}>
                        {store.deviceFw !== '0.0.0' && (
                            <DeviceRow label="Версия прошивки">
                                <Typography variant="body2" fontWeight={500}>
                                    {store.deviceFw}
                                </Typography>
                            </DeviceRow>
                        )}

                        {store.logConfig && logConfigStr(store.logConfig) !== '' && (
                            <DeviceRow label="Интервал логирования">
                                <Typography variant="body2" fontWeight={500}>
                                    {logConfigStr(store.logConfig)}
                                </Typography>
                            </DeviceRow>
                        )}

                        <DeviceRow label="Последний выход на связь">
                            <Tooltip title={lastTransactionTooltip} arrow>
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{ borderBottom: '1px dashed', borderColor: 'divider' }}
                                >
                                    {lastTransactionRelative}
                                </Typography>
                            </Tooltip>
                            <Box
                                aria-label="Состояние устройства"
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: statusColor,
                                    flexShrink: 0,
                                }}
                            />
                        </DeviceRow>

                        <DeviceRow label="Датчик">
                            {isKosaSensor ? (
                                <Box
                                    sx={{
                                        color: 'text.primary',
                                        display: 'contents',
                                        '& .MuiListItemButton-root': {
                                            p: 0,
                                            borderRadius: 0,
                                        },
                                    }}
                                >
                                    <Kosa
                                        id={store.sensorId ?? 0}
                                        selected={false}
                                        onClick={() =>
                                            onSelect({
                                                kind: 'kosa',
                                                kosaId: store.sensorId ?? 0,
                                                switchMode: true,
                                            })
                                        }
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" fontWeight={500}>
                                    {store.sensorName}
                                </Typography>
                            )}
                        </DeviceRow>

                        {store.sensorId !== null && store.sensorState !== null && (
                            <DeviceRow label="Состояние датчика">
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    color={
                                        store.sensorState === SensorState.SensorStateOk
                                            ? 'success.main'
                                            : 'warning.main'
                                    }
                                >
                                    {sensorStateStr(store.sensorState)}
                                </Typography>
                            </DeviceRow>
                        )}

                        {/* ─── Метрики с графиками ─────────────────────────────── */}
                        {deferredPoints.memory.length > 0 && (
                            <DeviceMetricRow
                                label="Память"
                                unit=""
                                value={formatBytes(lastValue(deferredPoints.memory) ?? 0)}
                                color="#4EAAEA"
                                points={deferredPoints.memory}
                            />
                        )}

                        {deferredPoints.battery.length > 0 && (
                            <DeviceMetricRow
                                label="Заряд батареи"
                                unit="%"
                                value={String(lastValue(deferredPoints.battery))}
                                color="#4FAD5B"
                                points={deferredPoints.battery}
                                maxValue={100}
                                minValue={0}
                            />
                        )}

                        {deferredPoints.rssi.length > 0 && (
                            <DeviceMetricRow
                                label="Уровень сигнала (RSSI)"
                                unit="дБм"
                                value={String(lastValue(deferredPoints.rssi))}
                                color="#EE8632"
                                points={deferredPoints.rssi}
                            />
                        )}

                        {deferredPoints.cpuTemp.length > 0 && (
                            <DeviceMetricRow
                                label="Температура CPU"
                                unit="°C"
                                value={String(lastValue(deferredPoints.cpuTemp))}
                                color="#D0327B"
                                points={deferredPoints.cpuTemp}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {isRoot && (
                <Box
                    sx={{
                        mt: 3,
                        mb: 3,
                        px: { xs: 2, md: 3 },
                        minWidth: 0,
                        maxWidth: isDesktop ? 720 : '100%',
                    }}
                >
                    <DeviceLogs deviceId={deviceId} deviceType={store.deviceType} />
                </Box>
            )}
        </Box>
    );
});

export default DeviceBoardLayout;
